import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { supabase } from './lib/supabase';
import AllSportsHubsLogo from './pages/ASH Logo (1).png';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [verificationMessage, setVerificationMessage] = useState('');
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [debugInfo, setDebugInfo] = useState('');

  const { login, loading, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  useEffect(() => {
    const handleEmailConfirmation = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const token_hash = urlParams.get('token_hash');
      const type = urlParams.get('type');
      
      if (token_hash && type) {
        try {
          const { error: verifyError } = await supabase.auth.verifyOtp({
            token_hash,
            type,
          });

          if (verifyError) {
            setVerificationMessage(`${type === 'signup' ? 'Email confirmation' : 'Password recovery'} failed: ${verifyError.message}`);
            console.error('Verification error:', verifyError);
          } else {
            if (type === 'signup') {
              setVerificationMessage('Email confirmed successfully! You can now log in.');
              console.log('Email verified successfully');
            } else if (type === 'recovery') {
              setVerificationMessage('Password recovery link verified. Please navigate to the reset page to set a new password.');
              console.log('Recovery token verified. Prompt user to set new password.');
            }
          }
        } catch (err) {
          setVerificationMessage('An error occurred during verification.');
          console.error('Unexpected verification error:', err);
        }
      }
    };

    handleEmailConfirmation();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError(result.message || 'Login failed.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error(err);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');
    try {
      const result = await signInWithGoogle();
      if (!result.success) {
        setError(result.message || 'Google sign-in failed.');
      }
    } catch (err) {
      setError('An unexpected error occurred with Google login.');
      console.error(err);
    }
  };

  const openForgotModal = () => {
    setResetEmail(formData.email || '');
    setResetMessage('');
    setDebugInfo('');
    setShowForgotModal(true);
  };

  const closeForgotModal = () => {
    setShowForgotModal(false);
    setResetLoading(false);
    setResetMessage('');
    setDebugInfo('');
  };

  const checkSupabaseSettings = async () => {
    console.log('=== DIAGNOSTIC CHECK START ===');
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      console.log('✓ Supabase connection: OK');
      console.log('Current session exists:', !!session);
    } catch (err) {
      console.error('✗ Supabase connection: FAILED', err);
    }

    try {
      const supabaseUrl = supabase.supabaseUrl || 'Not available';
      console.log('Supabase URL:', supabaseUrl);
    } catch (err) {
      console.error('Cannot read Supabase URL');
    }

    if (resetEmail) {
      console.log('Checking if user exists in database...');
      console.log('Target email:', resetEmail);
    }

    console.log('=== DIAGNOSTIC CHECK END ===');
  };

  const handleSendPasswordReset = async (e) => {
    e?.preventDefault?.();
    setResetMessage('');
    setError('');
    setDebugInfo('');

    if (!resetEmail) {
      setResetMessage('Please enter the email address you used to register.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(resetEmail)) {
      setResetMessage('Please enter a valid email address.');
      return;
    }

    setResetLoading(true);

    await checkSupabaseSettings();

    try {
      const redirectTo = `http://localhost:3000/ResetPassword`;
      
      console.log('==========================================');
      console.log('PASSWORD RESET ATTEMPT');
      console.log('==========================================');
      console.log('Timestamp:', new Date().toISOString());
      console.log('Email:', resetEmail);
      console.log('Redirect URL:', redirectTo);
      console.log('Origin:', window.location.origin);
      console.log('Current URL:', window.location.href);
      
      console.log('Supabase Config:');
      console.log('- URL:', supabase.supabaseUrl);
      console.log('- Has anon key:', !!supabase.supabaseKey);

      const startTime = performance.now();
      
      const { data, error: resetError } = await supabase.auth.resetPasswordForEmail(
        resetEmail, 
        { 
          redirectTo,
          captchaToken: undefined
        }
      );

      const endTime = performance.now();
      const duration = (endTime - startTime).toFixed(2);

      console.log('==========================================');
      console.log('RESET API RESPONSE');
      console.log('==========================================');
      console.log('Response time:', duration, 'ms');
      console.log('Response data:', data);
      console.log('Response error:', resetError);
      
      if (resetError) {
        console.error('==========================================');
        console.error('ERROR DETAILS');
        console.error('==========================================');
        console.error('Error message:', resetError.message);
        console.error('Error code:', resetError.code);
        console.error('Error status:', resetError.status);
        console.error('Full error object:', JSON.stringify(resetError, null, 2));
        
        setResetMessage(`Error: ${resetError.message}`);
        setDebugInfo(`Code: ${resetError.code || 'N/A'}, Status: ${resetError.status || 'N/A'}`);
      } else {
        console.log('==========================================');
        console.log('SUCCESS');
        console.log('==========================================');
        console.log('✓ API call successful');
        console.log('✓ No error returned');
        console.log('Data returned:', JSON.stringify(data, null, 2));
        
        console.log('');
        console.log('NEXT STEPS:');
        console.log('1. Check your email inbox (and spam folder)');
        console.log('2. Go to Supabase Dashboard → Authentication → Logs');
        console.log('3. Look for email delivery status');
        console.log('4. Check Authentication → Email Templates');
        console.log('5. Verify SMTP settings if configured');
        console.log('');
        
        setResetMessage('If that email exists in our system, a password reset link has been sent. Please check your inbox and spam folder.');
        setDebugInfo('✓ Request successful - Check console for full logs');
        
        console.log('Checking email configuration...');
        try {
          console.log('Email service: ' + (data?.email_sent ? 'Email queued/sent' : 'Unknown status'));
        } catch (e) {
          console.log('Cannot determine email service status');
        }
      }
      
      console.log('==========================================');

    } catch (err) {
      console.error('==========================================');
      console.error('UNEXPECTED ERROR');
      console.error('==========================================');
      console.error('Error type:', err.constructor.name);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      console.error('Full error:', err);
      console.error('==========================================');
      
      setResetMessage('An unexpected error occurred. Check console for details.');
      setDebugInfo(`Error: ${err.message}`);
    } finally {
      setResetLoading(false);
    }
  };

  const GoogleButton = () => (
    <button 
      onClick={handleGoogleLogin}
      disabled={loading}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        backgroundColor: 'white',
        border: '1px solid #dadce0',
        borderRadius: '25px',
        padding: '12px 16px',
        fontSize: '14px',
        fontWeight: '500',
        color: '#3c4043',
        cursor: loading ? 'not-allowed' : 'pointer',
        marginBottom: '1.5rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        transition: 'all 0.2s',
        opacity: loading ? 0.6 : 1
      }}
      onMouseEnter={(e) => {
        if (!loading) {
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
          e.currentTarget.style.backgroundColor = '#f8f9fa';
        }
      }}
      onMouseLeave={(e) => {
        if (!loading) {
          e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
          e.currentTarget.style.backgroundColor = 'white';
        }
      }}
    >
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      Sign in with Google
    </button>
  );

  const Divider = () => (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      margin: '1.5rem 0',
      gap: '1rem'
    }}>
      <div style={{ flex: 1, height: '1px', backgroundColor: '#dadce0' }}></div>
      <span style={{ color: '#5f6368', fontSize: '14px' }}>or</span>
      <div style={{ flex: 1, height: '1px', backgroundColor: '#dadce0' }}></div>
    </div>
  );

  const handleLogoClick = () => {
    navigate('/');
  };

  return (
    <div className="auth-container animate-fade-in-up" style={{ maxWidth: 420, margin: '0 auto', padding: '2rem' }}>
      <div 
        style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginBottom: '1.5rem', 
          cursor: 'pointer' 
        }}
        onClick={handleLogoClick}
      >
        <img 
          src={AllSportsHubsLogo} 
          alt="All Sports Hubs Logo" 
          style={{ height: '125px', width: 'auto', objectFit: 'contain' }} 
        />
      </div>

      <h2 style={{ marginBottom: '1rem', color: '#1e74c7', textAlign: 'center' }}>Login</h2>

      {verificationMessage && (
        <div style={{ 
          padding: '10px', 
          marginBottom: '15px',
          backgroundColor: verificationMessage.toLowerCase().includes('success') ? '#d4edda' : '#f8d7da',
          color: verificationMessage.toLowerCase().includes('success') ? '#155724' : '#721c24',
          border: '1px solid',
          borderColor: verificationMessage.toLowerCase().includes('success') ? '#c3e6cb' : '#f5c6cb',
          borderRadius: '4px'
        }}>
          {verificationMessage}
        </div>
      )}

      {error && (
        <div style={{ 
          color: 'red', 
          backgroundColor: '#ffebee', 
          padding: '10px', 
          borderRadius: '4px', 
          marginBottom: '1rem',
          border: '1px solid #ffcdd2'
        }}>
          {error}
        </div>
      )}

      <GoogleButton />
      <Divider />

      <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column' }}>
        <input
          type="email"
          placeholder="Email"
          required
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          style={{ 
            marginBottom: '1rem', 
            fontSize: '1rem',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #cfe9ff',
            opacity: loading ? 0.6 : 1
          }}
          disabled={loading}
        />

        <input
          type="password"
          placeholder="Password"
          required
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          style={{ 
            marginBottom: '0.5rem',
            fontSize: '1rem',
            padding: '12px',
            borderRadius: '8px',
            border: '1px solid #cfe9ff',
            opacity: loading ? 0.6 : 1
          }}
          disabled={loading}
        />

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.25rem' }}>
          <button
            type="button"
            onClick={openForgotModal}
            disabled={loading}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#1e74c7',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              padding: '4px 8px',
              textDecoration: 'underline'
            }}
          >
            Forgot Password?
          </button>
        </div>

        <button 
          type="submit" 
          className="register-button" 
          style={{ 
            padding: '0.8rem', 
            fontSize: '1.1rem',
            borderRadius: 28,
            background: 'linear-gradient(90deg,#1e74c7,#7fc3f2)',
            color: 'white',
            border: 'none',
            boxShadow: '0 6px 18px rgba(30,116,199,0.18)',
            opacity: loading ? 0.6 : 1,
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
          disabled={loading}
        >
          {loading ? 'Logging in...' : 'Login'}
        </button>
      </form>

      <p style={{ marginTop: '1.5rem', color: '#2c3e50', textAlign: 'center' }}>
        Don't have an account?{' '}
        <span
          onClick={() => !loading && navigate('/register')}
          className="switch-link"
          style={{ 
            cursor: loading ? 'not-allowed' : 'pointer',
            color: '#1e74c7'
          }}
        >
          Register
        </span>
      </p>

      {showForgotModal && (
        <>
          <div
            onClick={closeForgotModal}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.4)',
              zIndex: 60
            }}
          />
<div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%,-50%)',
              background: 'white',
              padding: '1.25rem',
              borderRadius: '10px',
              width: 'min(420px, 92vw)',
              zIndex: 70,
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
            }}
          >
            <button
              onClick={closeForgotModal}
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                background: 'transparent',
                border: 'none',
                fontSize: '20px',
                color: '#9ca3af',
                cursor: 'pointer',
                padding: '0',
                lineHeight: '1',
                transition: 'color 0.2s',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#374151'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
              aria-label="Close modal"
            >
              ×
            </button>
            <h3 style={{ marginTop: 0, marginBottom: '0.5rem', color: '#1e74c7', paddingRight: '30px' }}>Reset password</h3>
            <p style={{ marginTop: 0, marginBottom: '0.75rem', color: '#374151', fontSize: 14 }}>
              Enter your account email and we'll send a password reset link.
            </p>

            <form onSubmit={handleSendPasswordReset} style={{ display: 'flex', flexDirection: 'column' }}>
              <input
                type="email"
                placeholder="Email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                style={{
                  padding: '10px',
                  borderRadius: 8,
                  border: '1px solid #e6eefc',
                  marginBottom: '0.75rem'
                }}
                required
              />

              {resetMessage && (
                <div style={{ 
                  marginBottom: '0.75rem', 
                  color: resetMessage.toLowerCase().includes('error') ? '#721c24' : '#155724', 
                  backgroundColor: resetMessage.toLowerCase().includes('error') ? '#f8d7da' : '#d4edda', 
                  padding: 8, 
                  borderRadius: 6,
                  fontSize: '13px'
                }}>
                  {resetMessage}
                </div>
              )}

              {debugInfo && (
                <div style={{
                  marginBottom: '0.75rem',
                  padding: '8px',
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  color: '#0c4a6e'
                }}>
                  {debugInfo}
                </div>
              )}

              <div style={{ 
                marginBottom: '0.75rem',
                padding: '8px',
                backgroundColor: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: '6px',
                fontSize: '11px'
              }}>
                <strong>Tip:</strong> Open browser console (F12) for detailed logs
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="submit"
                  disabled={resetLoading}
                  style={{
                    flex: 1,
                    padding: '0.6rem 0.75rem',
                    borderRadius: 8,
                    border: 'none',
                    background: '#1e74c7',
                    color: '#fff',
                    cursor: resetLoading ? 'not-allowed' : 'pointer'
                  }}
                >
                  {resetLoading ? 'Sending...' : 'Send reset link'}
                </button>
                <button
                  type="button"
                  onClick={closeForgotModal}
                  style={{
                    padding: '0.6rem 0.75rem',
                    borderRadius: 8,
                    border: '1px solid #d1d5db',
                    background: '#fff',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
};

export default Login;